<template>
    <v-container>
        <v-progress-linear indeterminate v-if="loading" aria-label="Loading"></v-progress-linear>
        <v-card class="mb-4" v-if="!loading">
            <v-card-title><h1 class="headline">Register to Testify</h1></v-card-title>
            <v-card-text>
                <strong>Title:</strong> {{ meetingInfo.Title }}<br />
                <strong>Date:</strong> {{ DatePretty(meetingInfo.DocumentDate) }} <span v-if="relativeTime">({{relativeTime}})</span> <span v-if="meetingInfo.DocumentDateComment">{{meetingInfo.DocumentDateComment}}</span><br />
                <strong>Room:</strong> {{ meetingInfo.Room }}<br />
            </v-card-text>
        </v-card>

        <v-alert v-if="meetingInfo.DocumentDate && IsMeetingTimePast && !IsFourHoursPast" type="warning">This meeting is currently ongoing.</v-alert>
        <v-alert v-if="meetingInfo.DocumentDate && IsFourHoursPast" type="error">Registration is no longer available for this meeting.</v-alert>

        <v-form v-if="!loading && !success && !IsFourHoursPast" ref="form" v-model="valid" @submit.prevent="submitForm">
            <v-row>
                <v-col cols="6">
                    <v-text-field v-model.trim="testifierPending.name"
                                  label="Name"
                                  name="Name"
                                  required
                                  autocapitalize="words"
                                  hint="Enter your full name as it should appear in the record. Example: John Doe. Please do not include titles or honorifics."
                                  :rules="[v => !!v || 'Name is required', v => v.trim().length > 0 || 'Name cannot be empty or just whitespace']"
                                  @blur="loadRepresentingOptions"
                                  autocomplete="name">
                    </v-text-field>
                    <v-alert v-if="showNameAlert"
                             dense
                             type="warning"
                             prominent>
                        Please provide both a first and last name (e.g., 'John Doe').
                    </v-alert>
                    <v-alert v-if="showCaptializationAlert"
                             dense
                             type="info">
                        The name you entered appeared to be all lowercase or all uppercase. We adjusted the capitalization automatically. Please review and confirm it looks correct.
                    </v-alert>
                    <v-alert v-if="showTitleRemovedAlert"
                             dense
                             type="info">
                        Titles like 'Senator' or 'Representative' are not required in the Name field. We removed the title automatically.  Please review the name for accuracy.
                    </v-alert>
                </v-col>

                <v-col cols="6">
                    <v-text-field v-model.trim="testifierPending.RegistrationNumber"
                                  label="Lobbyist Registration Number (Optional)"
                                  name="LobbyistRegistrationNumber"
                                  hint="If you are a registered lobbyist the Registered Lobbyist Number provided by the South Dakota Secretary of State. https://sosenterprise.sd.gov/businessservices/lobbyist/default.aspx"></v-text-field>
                    <!--autocomplete="lobbyistregistrationnumber"-->
                </v-col>
            </v-row>

            <div>How would you like to join the meeting?</div>
            <v-radio-group v-model="testifierPending.RegistrationTypeId"
                           :rules="[v => v !== null || 'Requested Meeting Attendance Type is required.']"
                           required
                           id="registration-type"
                           row>
                <v-radio label="In Person" :value="1"></v-radio>
                <v-radio label="Remotely" :value="2"></v-radio>
                <v-radio label="Written" :value="3"></v-radio>
            </v-radio-group>

            <v-text-field v-model.trim="testifierPending.email"
                          :label="testifierPending.RegistrationTypeId !== 1 ? 'Email' : 'Email (Optional)'"
                          name="Email"
                          type="email"
                          autocomplete="email"
                          @blur="loadRepresentingOptions"
                          :rules="testifierPending.RegistrationTypeId !== 1
                          ? [
                          v=>
                !!v || 'Email is required for remote and written registrations',
                v => /.+@.+/.test(v) || 'Email must be valid',
                v => !v || /^[^@]+@((([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,})|(\[(\d{1,3}\.){3}\d{1,3}\])|(\[IPv6:[0-9a-fA-F:]+\]))$/.test(v)
                || 'Email must be valid',
                v => !v || v.length <= 320 || 'Email must be 320 characters or fewer',
                v => !v || EmailTestRegexPattern
                .test(v) || 'Please enter a single valid email address'
                ]
                : [
                v => !v || /.+@.+/.test(v) || 'Email must be valid',
                v => !v || /^[^@]+@((([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,})|(\[(\d{1,3}\.){3}\d{1,3}\])|(\[IPv6:[0-9a-fA-F:]+\]))$/.test(v)
                || 'Email must be valid',
                v => !v || v.length <= 320 || 'Email must be 320 characters or fewer',
                v => !v || EmailTestRegexPattern
                .test(v) || 'Please enter a single valid email address'
                ]">
            </v-text-field>

            <v-combobox v-model.trim="testifierPending.representing"
                        label="Representing"
                        name="Representing"
                        autocapitalize="words"
                        :items="representingOptions"
                        item-text="Employer"
                        item-value="Employer"
                        :return-object="false"
                        @change="onRepresentingChange"
                        @blur="validateRepresenting"
                        hint="The entity you are employed by, representing, or 'Self' if representing yourself."
                        autocomplete="organization"
                        required
                        :rules="[v => !!v || 'Who you are representing is required']"
                        clearable></v-combobox>

            <v-alert v-if="SetRegistrationNumber" type="info">We've set your Lobbyist Registration number for you based on your selection.</v-alert>
            <v-alert v-if="showRepresentingCapitalizationAlert"
                     dense
                     type="info">
                The organization you entered appeared to be all lowercase or all uppercase. We adjusted the capitalization automatically. Please review and confirm it looks correct.
            </v-alert>

            <v-text-field v-model.trim="testifierPending.city"
                          label="City"
                          name="City"
                          autocapitalize="words"
                          :rules="[v => !!v || 'City is required']"
                          hint="The city you are from"
                          autocomplete="address-level2"></v-text-field>

            <v-autocomplete v-model="testifierPending.state"
                            hint="The state you are from."
                            :items="States"
                            item-text="name"
                            item-value="abbreviation"
                            autocomplete="address-level1"
                            :rules="[v => !!v || 'State is required', v => States.some(state => state.abbreviation === v) || 'Please select a valid state']"
                            required
                            clearable
                            label="State" />

            <!--<v-text-field v-model.trim="testifierPending.country"
                          label="Country"
                          name="country"
                          autocapitalize="words"
                          :rules="[v => !!v || 'Country is required']"
                          hint="The country you are from"
                          autocomplete="country-name"></v-text-field>-->

            <div>(Note: We will seek approval from the Chair for any remote requests.)</div>

            <v-divider class="my-4"></v-divider>

            <v-card class="mb-4" v-for="(item, index) in agendaItems" :key="index">
                <v-card-title v-if="item.Title && !CustomAgenda" v-html="item.Title" class="pl-3 pt-0" style="display: block;"></v-card-title>
                <v-card-subtitle v-if="item.Subtitle && !CustomAgenda" v-html="item.Subtitle"></v-card-subtitle>
                <v-text-field density="compact" v-if="CustomAgenda" v-model.trim="item.Title" label="Topic you wish you testify on." required></v-text-field>

                <v-card-text>
                    <v-radio-group v-model="item.testimony" id="testimony-type" row>
                        <v-radio label="For" value="For"></v-radio>
                        <v-radio label="Against" value="Against"></v-radio>
                        <v-radio label="Presenting" value="Presenting"></v-radio>
                        <v-radio label="Other" value="Other"></v-radio>
                        <v-radio label="Not Applicable" value="N/A"></v-radio>
                    </v-radio-group>

                    <v-textarea v-model.trim="item.writtenText"
                                label="Written comments for this agenda item (optional)"
                                auto-grow
                                rows="1"
                                dense
                                :counter="4000"
                                :rules="[v => String(v || '').length <= 4000 || 'Max 4000 characters']"
                                placeholder="Enter written testimony for this specific agenda item...">
                    </v-textarea>

                    <v-file-input v-model="item.attachmentsFiles"
                                  label="Supporting documents for this agenda item (optional)"
                                  show-size
                                  counter
                                  multiple
                                  dense
                                  chips
                                  accept="
                                    .pdf,application/pdf,
                                    .xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,
                                    .docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document,
                                    .pptx,application/vnd.openxmlformats-officedocument.presentationml.presentation,
                                    .txt,text/plain,
                                    .rtf,application/rtf,
                                    .png,image/png,
                                    .jpg,.jpeg,image/jpeg,
                                    .heic,image/heic,
                                    .heif,image/heif,
                                    .webp,image/webp"
                                  hint="Accepts PDF, Word, Excel, PowerPoint, Text, and Images (PNG, JPG, HEIC, WEBP). Total attachments across all agenda items must be 25 MB or less."
                                  :rules="[files => totalAttachmentsRule(index, files)]"
                                  persistent-hint
                                  class="mt-2">
                    </v-file-input>
                </v-card-text>
            </v-card>

            <template v-if="!IsMeetingTimePast">
                <v-divider class="my-6"></v-divider>
                <div class="mb-1">Americans with Disabilities Act (ADA) Request</div>
                <div>Individuals needing assistance, pursuant to the Americans with Disabilities Act should submit this 48 hours before convening to allow time to make any necessary arrangements.</div>
                <v-textarea v-model.trim="testifierPending.Ada"
                            label="ADA Request (Optional)"
                            hint="Describe any accommodation you need (for example, a sign language interpreter). Leave blank if no assistance is required."
                            auto-grow
                            rows="1"
                            :counter="4000"
                            :rules="[v => String(v || '').length <= 4000 || 'Max 4000 characters']"
                            placeholder="Example: Sign Language Interpreter">
                </v-textarea>
            </template>

            <v-btn :disabled="!valid || loading || saving" color="success" class="mt-4" @click="submitForm">Submit Registration</v-btn>
        </v-form>

        <v-alert type="error" v-if="RequiredTestimony">Please respond For, Against, or Presenting to at least one option and then submit your registration.</v-alert>

        <v-alert v-if="success" type="success">
            Registration submitted successfully!
            <v-progress-linear v-model="progressBarWidth" aria-label="Loading"></v-progress-linear>
        </v-alert>
        <v-alert v-if="error" type="error">
            Failed to submit registration. {{ errorMessage }}
        </v-alert>
    </v-container>
</template>

<script>
    import moment from 'moment'
    import { DateTime } from 'luxon';

    export default {
        name: 'Testify',
        props: ['AgendaId'],
        components: {},
        methods: {
            LuxonNow(add) { return (add == null || add == 0) ? DateTime.now() : DateTime.now().plus(add); },
            LuxonDateify(jsDate) { return DateTime.fromJSDate(jsDate); },

            async LoadData() {
                this.LoadedTime = new Date();
                this.testifierPending.DocumentId = this.AgendaId;
                this.loading = true;
                try {
                    if (this.AgendaId == undefined || this.AgendaId < 1) {
                        this.loading = false;
                        this.$router.go(-1);
                    }
                    const response = await fetch('/api/Testify/' + this.AgendaId, {
                        method: 'GET',
                        credentials: 'include',
                        headers: { 'Content-Type': 'application/json' },
                    });

                    this.loading = false;
                    if (response.ok) {
                        const data = await response.json();
                        this.meetingInfo = {
                            Title: data.Title,
                            DocumentDate: data.DocumentDate,
                            LuxonDate: DateTime.fromISO(data.DocumentDate),
                            DocumentDateComment: data.DocumentDateComment,
                            Room: data.Room
                        };
                        if (data.Name) this.testifierPending.name = data.Name;
                        if (data.Email) this.testifierPending.email = data.Email;

                        this.agendaItems = data.AgendaItems.map(agenda => ({
                            Title: agenda.Title,
                            Subtitle: agenda.Subtitle,
                            testimony: null,
                            writtenText: '',
                            attachmentsFiles: [],
                            ActionlogId: agenda.ActionLogId,
                            BillId: agenda.BillId,
                        }));

                        this.CustomAgenda = false;
                        if (this.agendaItems.length === 0) {
                            this.CustomAgenda = true;
                            this.agendaItems = [{
                                Title: '',
                                Subtitle: null,
                                testimony: null,
                                writtenText: '',
                                attachmentsFiles: [],
                                ActionlogId: null,
                                BillId: null
                            }];
                        }

                        this.OneMinuteProcess();
                    } else {
                        throw new Error(response.statusText);
                    }
                } catch (error) {
                    this.loading = false;
                    console.error('Error loading data:', error);
                }
            },

            MeetingTimePretty(Time) { return Time != undefined ? moment(Time).format('h:mm A') : null; },
            DatePretty(Date) { return Date != undefined ? moment(Date).format('M/DD h:mm A') : null; },
            MinDatePretty(Date) { return Date != undefined ? moment(Date).format('M/DD') : null; },

            async loadRepresentingOptions() {
                if (!this.testifierPending.name) return;
                this.validateName();
                try {
                    this.loadingEntities = true;
                    const response = await fetch(`/api/testify/matching-entities?name=${encodeURIComponent(this.testifierPending.name)}`);
                    if (response.ok) {
                        const data = await response.json();
                        let selfoption = [{ Employer: 'Self', RegistrationNumber: null }];
                        this.representingOptions = data.concat(selfoption);
                        if (this.representingOptions && this.representingOptions.length > 1 && !this.testifierPending.representing) {
                            let firstLobbyOrg = this.representingOptions[0];
                            if (firstLobbyOrg) {
                                this.testifierPending.representing = firstLobbyOrg.Employer;
                                this.testifierPending.RegistrationNumber = firstLobbyOrg.RegistrationNumber;
                            }
                        }
                    } else {
                        this.representingOptions = [{ Employer: 'Self', RegistrationNumber: null }];
                    }
                } catch (error) {
                    console.error('Error fetching representing options:', error);
                    this.representingOptions = [];
                } finally {
                    this.loadingEntities = false;
                }
            },

            validateRepresenting() {
                if (this.testifierPending && this.testifierPending.representing) {
                    if (!this.representingCapitalizedAlready) {
                        const val = this.testifierPending.representing;
                        const isAllLower = val === val.toLowerCase();
                        const isAllUpper = val === val.toUpperCase();

                        if (isAllLower || isAllUpper) {
                            this.testifierPending.representing = val
                                .toLowerCase()
                                .replace(/\b[\p{L}']+/gu, w => w.charAt(0).toUpperCase() + w.slice(1));

                            this.showRepresentingCapitalizationAlert = true;
                            this.representingCapitalizedAlready = true;
                        }
                    }
                }
            },

            onRepresentingChange(selected) {
                this.SetRegistrationNumber = false;
                if (this.representingOptions && this.representingOptions.length > 0) {
                    const selectedEntity = this.representingOptions.find(option => option.Employer === selected);
                    if (selectedEntity && selectedEntity) {
                        this.testifierPending.RegistrationNumber = selectedEntity.RegistrationNumber;
                        if (this.testifierPending.RegistrationNumber) {
                            this.SetRegistrationNumber = true;
                        }
                    }
                }
                if (this.testifierPending.representing && this.testifierPending.representing != 'Self') {
                    this.UnregisteredLobbyist = !this.representingOptions.some(option => option.Employer === this.testifierPending.representing);
                } else {
                    this.UnregisteredLobbyist = false;
                }
            },

            stripHtml(html) {
                return html ? String(html).replace(/<\/?[^>]+(>|$)/g, '').trim() : '';
            },

            totalAttachmentsRule(currentIndex, currentFiles) {
                var total = 0;
                this.agendaItems.forEach(function (item, idx) {
                    var files = (idx === currentIndex) ? (currentFiles || []) : (item.attachmentsFiles || []);
                    total += Array.from(files).reduce(function (s, f) {
                        return s + ((f && f.size) ? f.size : 0);
                    }, 0);
                });
                return total <= 25 * 1024 * 1024 || 'Total attachments across all agenda items must be 25 MB or less.';
            },

            async buildIncomingDocumentsFromAgenda() {
                var incoming = [];

                for (var i = 0; i < this.agendaItems.length; i++) {
                    var item = this.agendaItems[i];
                    var title = this.stripHtml(item.Title) || 'Agenda Item';

                    // Written text -> .txt doc
                    if (item.writtenText && item.writtenText.trim()) {
                        var text = item.writtenText.trim();
                        incoming.push({
                            FileName: title + '.txt',
                            ContentType: 'text/plain',
                            Size: text.length,
                            ActionLogId: (item.ActionlogId !== undefined && item.ActionlogId !== null) ? item.ActionlogId : null,
                            BillId: (item.BillId !== undefined && item.BillId !== null) ? item.BillId : null,
                            Base64: this.utf8ToBase64(text)
                        });
                    }

                    // Uploaded attachments
                    var files = Array.from(item.attachmentsFiles || []).slice(0, 10);
                    for (var j = 0; j < files.length; j++) {
                        var f = files[j];
                        if (!f) continue;

                        incoming.push({
                            FileName: f.name || 'attachment',
                            ContentType: f.type || 'application/octet-stream',
                            Size: f.size || 0,
                            ActionLogId: (item.ActionlogId !== undefined && item.ActionlogId !== null) ? item.ActionlogId : null,
                            BillId: (item.BillId !== undefined && item.BillId !== null) ? item.BillId : null,
                            Base64: await this.readFileAsBase64(f)
                        });
                    }
                }

                return incoming;
            },

            async submitForm() {
                this.RequiredTestimony = !this.agendaItems.some(function (item) {
                    return ['For', 'Against', 'Presenting', 'Other'].indexOf(item.testimony) !== -1;
                });
                if (this.RequiredTestimony) return;

                if (!this.$refs.form.validate()) return;

                try {
                    this.saving = true;

                    // Keep testimonyData clean (no files)
                    var testimonyData = this.agendaItems.map(function (a) {
                        return {
                            Title: a.Title,
                            testimony: a.testimony,
                            ActionlogId: (a.ActionlogId !== undefined && a.ActionlogId !== null) ? a.ActionlogId : null,
                            BillId: (a.BillId !== undefined && a.BillId !== null) ? a.BillId : null
                        };
                    });
                    this.testifierPending.testimonyData = JSON.stringify(testimonyData);

                    // Build IncomingDocument[] per agenda item
                    var documents = await this.buildIncomingDocumentsFromAgenda();

                    // 1 = In Person, 2 = Remote, 3 = Written
                    this.testifierPending.InPerson = (this.testifierPending.RegistrationTypeId == 1);
                    this.testifierPending.WrittenOnly = (this.testifierPending.RegistrationTypeId == 3);

                    var payloadObject = Object.assign({}, this.testifierPending, {
                        WrittenTestimonyText: null, // written text is sent as .txt IncomingDocuments
                        Documents: documents
                    });

                    var response = await fetch('/api/Testify/', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify(payloadObject)
                    });

                    this.saving = false;

                    if (response.ok) {
                        this.success = true;
                        this.error = false;
                        this.showNameAlert = false;
                        this.captializedNameForUserAlready = false;
                        this.showRepresentingCapitalizationAlert = false;
                        this.representingCapitalizedAlready = false;
                        this.showTitleRemovedAlert = false;
                        this.titleRemovedAlready = false;
                        this.SetRegistrationNumber = false;

                        this.testifierPending = {
                            DocumentId: this.AgendaId,
                            name: '',
                            city: '',
                            state: 'SD',
                            country: 'United States of America',
                            representing: '',
                            email: '',
                            testimonyData: '',
                            RegistrationTypeId: 1,
                            RegistrationNumber: '',
                            Ada: '',
                            InPerson: true,
                            WrittenOnly: false
                        };

                        this.agendaItems.forEach(function (item) {
                            item.testimony = null;
                            item.writtenText = '';
                            item.attachmentsFiles = [];
                        });

                        this.representingOptions = [{ Employer: 'Self', RegistrationNumber: null }];

                        this.startCountdown();
                    } else {
                        this.error = true;
                        this.success = false;
                        var data = await response.text();
                        this.errorMessage = data;
                        console.error('Error submitting form:', data);
                    }
                } catch (err) {
                    this.error = true;
                    this.success = false;
                    this.saving = false;
                    this.errorMessage = 'Unexpected error submitting registration.';
                    console.error(err);
                } finally {
                    this.saving = false;
                }
            },

            utf8ToBase64: function (str) {
                try {
                    return btoa(unescape(encodeURIComponent(str || '')));
                } catch (e) {
                    var safe = String(str || '').replace(/[^\x00-\x7F]/g, '?');
                    return btoa(safe);
                }
            },

            readFileAsBase64: function (file) {
                return new Promise(function (resolve, reject) {
                    var reader = new FileReader();

                    reader.onload = function () {
                        var result = reader.result;

                        if (result && result.byteLength !== undefined) {
                            var bytes = new Uint8Array(result);
                            var binary = '';
                            var chunk = 0x8000;

                            for (var i = 0; i < bytes.length; i += chunk) {
                                binary += String.fromCharCode.apply(null, bytes.subarray(i, i + chunk));
                            }

                            resolve(btoa(binary));
                            return;
                        }

                        var dataUrl = String(result || '');
                        var idx = dataUrl.indexOf('base64,');
                        resolve(idx !== -1 ? dataUrl.substring(idx + 7) : dataUrl);
                    };

                    reader.onerror = function (err) { reject(err); };

                    if (reader.readAsArrayBuffer) reader.readAsArrayBuffer(file);
                    else reader.readAsDataURL(file);
                });
            },


            OneMinuteProcess() {
                if (!this.meetingInfo.DocumentDate) return;
                this.IsMeetingTimePast = DateTime.now() > DateTime.fromISO(this.meetingInfo.DocumentDate);
                this.relativeTime = DateTime.fromISO(this.meetingInfo.DocumentDate).toRelative();
                this.IsFourHoursPast = DateTime.now() > DateTime.fromISO(this.meetingInfo.DocumentDate).plus({ hours: 12 });
            },

            startCountdown() {
                this.countdown = 5;
                if (this.CountdownInterval) clearInterval(this.CountdownInterval);
                this.CountdownInterval = setInterval(() => {
                    if (this.countdown > 0) {
                        this.countdown--;
                    } else {
                        this.success = false;
                        clearInterval(this.CountdownInterval);
                    }
                }, 1000);
            },

            validateName() {
                if (this.testifierPending && this.testifierPending.name) {
                    const nameParts = this.testifierPending.name.split(' ').filter(Boolean);
                    this.showNameAlert = nameParts.length < 2;

                    if (!this.titleRemovedAlready) {
                        const original = String(this.testifierPending.name).trim();

                        const titleOnceRe = /\b(senator|sen\.|sen|representative|rep\.|rep)\b\.?/i;
                        const cleaned = original
                            .replace(titleOnceRe, '')
                            .replace(/\s{2,}/g, ' ')
                            .trim();

                        if (cleaned !== original) {
                            this.testifierPending.name = cleaned;
                            this.showTitleRemovedAlert = true;
                            this.titleRemovedAlready = true;
                        } else {
                            this.showTitleRemovedAlert = false;
                        }
                    }

                    if (!this.showNameAlert && !this.captializedNameForUserAlready) {
                        const name = this.testifierPending.name;
                        const isAllLower = name === name.toLowerCase();
                        const isAllUpper = name === name.toUpperCase();
                        if (!isAllLower && !isAllUpper) return;

                        this.testifierPending.name = name
                            .toLowerCase()
                            .replace(/\b[\p{L}']+/gu, w => w.charAt(0).toUpperCase() + w.slice(1))
                            .replace(/\bMc(\p{L})/gu, (_, c) => "Mc" + c.toUpperCase());

                        this.showCaptializationAlert = true;
                        this.captializedNameForUserAlready = true;
                    }
                }
            }
        },

        data: function () {
            return {
//                EmailTestRegexPattern: new RegExp(
///^(?<localPart>(?<dotString>[0-9a-z!#$%&'*+\-\/=?^_`\{|\}~\u{80}-\u{10FFFF}]+(\.[0-9a-z!#$%&'*+\-\/=?^_`\{|\}~\u{80}-\u{10FFFF}]+)*)|(?<quotedString>"([\x20-\x21\x23-\x5B\x5D-\x7E\u{80}-\u{10FFFF}]|\\[\x20-\x7E])*"))(?<!.{64,})@(?<domainOrAddressLiteral>(?<addressLiteral>\[((?<IPv4>\d{1,3}(\.\d{1,3}){3})|(?<IPv6Full>IPv6:[0-9a-f]{1,4}(:[0-9a-f]{1,4}){7})|(?<IPv6Comp>IPv6:([0-9a-f]{1,4}(:[0-9a-f]{1,4}){0,5})?::([0-9a-f]{1,4}(:[0-9a-f]{1,4}){0,5})?)|(?<IPv6v4Full>IPv6:[0-9a-f]{1,4}(:[0-9a-f]{1,4}){5}:\d{1,3}(\.\d{1,3}){3})|(?<IPv6v4Comp>IPv6:([0-9a-f]{1,4}(:[0-9a-f]{1,4}){0,3})?::([0-9a-f]{1,4}(:[0-9a-f]{1,4}){0,3}:)?\d{1,3}(\.\d{1,3}){3})|(?<generalAddressLiteral>[a-z0-9\-]*[[a-z0-9]:[\x21-\x5A\x5E-\x7E]+))\])|(?<Domain>(?!.{256,})(([0-9a-z\u{80}-\u{10FFFF}]([0-9a-z\-\u{80}-\u{10FFFF}]*[0-9a-z\u{80}-\u{10FFFF}])?))(\.([0-9a-z\u{80}-\u{10FFFF}]([0-9a-z\-\u{80}-\u{10FFFF}]*[0-9a-z\u{80}-\u{10FFFF}])?))*))$/iu
                //                ),
                EmailTestRegexPattern: new RegExp(
                    /^(?:(?:[0-9a-z!#$%&'*+\-\/=?^_`\{|\}~\u{80}-\u{10FFFF}]+(\.[0-9a-z!#$%&'*+\-\/=?^_`\{|\}~\u{80}-\u{10FFFF}]+)*)|(?:"([\x20-\x21\x23-\x5B\x5D-\x7E\u{80}-\u{10FFFF}]|\\[\x20-\x7E])*"))(?<!.{64,})@(?:(?:\[((?:\d{1,3}(\.\d{1,3}){3})|(?:IPv6:[0-9a-f]{1,4}(:[0-9a-f]{1,4}){7})|(?:IPv6:([0-9a-f]{1,4}(:[0-9a-f]{1,4}){0,5})?::([0-9a-f]{1,4}(:[0-9a-f]{1,4}){0,5})?)|(?:IPv6:[0-9a-f]{1,4}(:[0-9a-f]{1,4}){5}:\d{1,3}(\.\d{1,3}){3})|(?:IPv6:([0-9a-f]{1,4}(:[0-9a-f]{1,4}){0,3})?::([0-9a-f]{1,4}(:[0-9a-f]{1,4}){0,3}:)?\d{1,3}(\.\d{1,3}){3})|(?:[a-z0-9\-]*[[a-z0-9]:[\x21-\x5A\x5E-\x7E]+))\])|(?:(?!.{256,})(([0-9a-z\u{80}-\u{10FFFF}]([0-9a-z\-\u{80}-\u{10FFFF}]*[0-9a-z\u{80}-\u{10FFFF}])?))(\.([0-9a-z\u{80}-\u{10FFFF}]([0-9a-z\-\u{80}-\u{10FFFF}]*[0-9a-z\u{80}-\u{10FFFF}])?))*))$/iu
                ),
                UnregisteredLobbyist: false,
                RequiredTestimony: false,
                SetRegistrationNumber: false,
                countdown: 0,
                CountdownInterval: null,
                IsMeetingTimePast: false,
                IsFourHoursPast: false,
                relativeTime: null,
                errorMessage: '',
                loading: true,
                saving: false,
                Agenda: {},
                TestifyData: {},
                valid: false,
                success: false,
                error: false,
                showNameAlert: false,
                showCaptializationAlert: false,
                captializedNameForUserAlready: false,
                showRepresentingCapitalizationAlert: false,
                representingCapitalizedAlready: false,
                showTitleRemovedAlert: false,
                titleRemovedAlready: false,


                CustomAgenda: false,

                testifierPending: {
                    DocumentId: null,
                    name: '',
                    city: '',
                    state: 'SD',
                    country: 'United States of America',
                    representing: '',
                    email: '',
                    testimonyData: '',
                    RegistrationTypeId: 1, // 1=In Person, 2=Remote, 3=Written
                    RegistrationNumber: '',
                    Ada: '',
                    InPerson: true,
                    WrittenOnly: false
                },

                agendaItems: [
                    { Title: '', testimony: null, writtenText: '', attachmentsFiles: [], ActionlogId: null, BillId: null },
                ],

                meetingInfo: {
                    Title: '',
                    DocumentDate: '',
                    DocumentDateComment: '',
                    Room: ''
                },

                States: [
                    { "name": "Alabama", "abbreviation": "AL" },
                    { "name": "Alaska", "abbreviation": "AK" },
                    { "name": "American Samoa", "abbreviation": "AS" },
                    { "name": "Arizona", "abbreviation": "AZ" },
                    { "name": "Arkansas", "abbreviation": "AR" },
                    { "name": "California", "abbreviation": "CA" },
                    { "name": "Colorado", "abbreviation": "CO" },
                    { "name": "Connecticut", "abbreviation": "CT" },
                    { "name": "Delaware", "abbreviation": "DE" },
                    { "name": "District Of Columbia", "abbreviation": "DC" },
                    { "name": "Federated States Of Micronesia", "abbreviation": "FM" },
                    { "name": "Florida", "abbreviation": "FL" },
                    { "name": "Georgia", "abbreviation": "GA" },
                    { "name": "Guam", "abbreviation": "GU" },
                    { "name": "Hawaii", "abbreviation": "HI" },
                    { "name": "Idaho", "abbreviation": "ID" },
                    { "name": "Illinois", "abbreviation": "IL" },
                    { "name": "Indiana", "abbreviation": "IN" },
                    { "name": "Iowa", "abbreviation": "IA" },
                    { "name": "Kansas", "abbreviation": "KS" },
                    { "name": "Kentucky", "abbreviation": "KY" },
                    { "name": "Louisiana", "abbreviation": "LA" },
                    { "name": "Maine", "abbreviation": "ME" },
                    { "name": "Marshall Islands", "abbreviation": "MH" },
                    { "name": "Maryland", "abbreviation": "MD" },
                    { "name": "Massachusetts", "abbreviation": "MA" },
                    { "name": "Michigan", "abbreviation": "MI" },
                    { "name": "Minnesota", "abbreviation": "MN" },
                    { "name": "Mississippi", "abbreviation": "MS" },
                    { "name": "Missouri", "abbreviation": "MO" },
                    { "name": "Montana", "abbreviation": "MT" },
                    { "name": "Nebraska", "abbreviation": "NE" },
                    { "name": "Nevada", "abbreviation": "NV" },
                    { "name": "New Hampshire", "abbreviation": "NH" },
                    { "name": "New Jersey", "abbreviation": "NJ" },
                    { "name": "New Mexico", "abbreviation": "NM" },
                    { "name": "New York", "abbreviation": "NY" },
                    { "name": "North Carolina", "abbreviation": "NC" },
                    { "name": "North Dakota", "abbreviation": "ND" },
                    { "name": "Northern Mariana Islands", "abbreviation": "MP" },
                    { "name": "Ohio", "abbreviation": "OH" },
                    { "name": "Oklahoma", "abbreviation": "OK" },
                    { "name": "Oregon", "abbreviation": "OR" },
                    { "name": "Palau", "abbreviation": "PW" },
                    { "name": "Pennsylvania", "abbreviation": "PA" },
                    { "name": "Puerto Rico", "abbreviation": "PR" },
                    { "name": "Rhode Island", "abbreviation": "RI" },
                    { "name": "South Carolina", "abbreviation": "SC" },
                    { "name": "South Dakota", "abbreviation": "SD" },
                    { "name": "Tennessee", "abbreviation": "TN" },
                    { "name": "Texas", "abbreviation": "TX" },
                    { "name": "Utah", "abbreviation": "UT" },
                    { "name": "Vermont", "abbreviation": "VT" },
                    { "name": "Virgin Islands", "abbreviation": "VI" },
                    { "name": "Virginia", "abbreviation": "VA" },
                    { "name": "Washington", "abbreviation": "WA" },
                    { "name": "West Virginia", "abbreviation": "WV" },
                    { "name": "Wisconsin", "abbreviation": "WI" },
                    { "name": "Wyoming", "abbreviation": "WY" }
                ],

                representingOptions: [{ Employer: 'Self', RegistrationNumber: null }],
                LoadedTime: new Date(),
                OneMinuteInterval: null,
                loadingEntities: false
            }
        },

        mounted: function () {
            this.LoadData();
            this.OneMinuteProcess();
            this.OneMinuteInterval = setInterval(this.OneMinuteProcess, 60000);
        },

        unmounted() {
            if (this.OneMinuteInterval) {
                clearInterval(this.OneMinuteInterval);
                this.OneMinuteInterval = null;
            }
            if (this.CountdownInterval) {
                clearInterval(this.CountdownInterval);
                this.CountdownInterval = null;
            }
        },

        watch: {
            AgendaId: function () {
                this.LoadData();
            }
        },

        computed: {
            currentRoute() { return this.$route.path; },
            LoggedIn() { return this.$LoggedIn; },
            progressBarWidth() {
                if (!this.countdown) return 0;
                return (this.countdown / 5) * 100;
            }
        },

        metaInfo() {
            const base = 'Register to Testify';
            const meetingTitle =
                this.meetingInfo &&
                    typeof this.meetingInfo.Title === 'string' &&
                    this.meetingInfo.Title.trim().length > 0
                    ? this.stripHtml(this.meetingInfo.Title).trim()
                    : null;

            return {
                title: meetingTitle
                    ? `${base} ${meetingTitle}`
                    : base
            };
        },
    };
</script>